/**
 * One message (docs/design/visual-overhaul.md §7.9, §7.10, §7.14): the
 * avatar or, grouped, a hover time in the gutter; the name in the top
 * role's colour; the reply line; text, images, reactions; the hover
 * toolbar; inline edit. The image lightbox lives here too (I26).
 *
 * Keyboard (I10): the message list is one Tab stop; a focused message
 * takes R (reply), E (edit your own), + (react), P (pin), Delete (delete),
 * and the menu key or Shift+F10 (its menu). The list's own arrow keys are
 * in ChannelView.
 */
import {
  MessageType,
  Permissions,
  RelationshipType,
  type Attachment,
  type Channel,
  type Message,
  type Snowflake,
} from '@neosocial/protocol';
import {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { useT } from '../i18n';
import { assetUrl } from '../lib/config';
import { useData } from '../store/data';
import { can, channelPermissions, colorRole, roleColorPair, userName } from '../store/selectors';
import { deleteMessage, editMessage } from '../store/messages';
import { api } from '../lib/api';
import { Avatar, Icon } from '../ui/kit';
import { RoleIcon } from './RoleIcon';
import { pushBack } from '@neosocial/ui/back';
import { readLayout } from '@neosocial/ui/layout';
import {
  confirmDialog,
  menusAsSheets,
  openMenu,
  portalRoot,
  pushEscape,
  toast,
} from '../ui/overlay';
import { Emoji, emojiName, isJumbo, openEmoji, pushRecent, quickReactions } from './emoji';
import { decodeCustomEmoji, encodeCustomEmoji } from './customEmoji';
import { usableCustomEmoji } from '../store/emoji';
import { plainText, renderMarkdown, type MentionResolver } from './markdown';
import { openProfile } from './ProfileCard';
import { CallMessage } from './CallMessage';
import { reportError } from './social';
import { openReport } from '../report/openReport';
import { revealMessage, useUm } from '../store/um';
import { useLightboxTouch } from './lightboxTouch';
import { useImageViewer } from './imageViewer';
import { openReactionList, ReactionChip } from './reactions';
import { useFlag } from '../store/config';
import { endPoll, pollOver } from '../store/polls';
import { PollResultNotice, PollView } from './Poll';
import { canForward, Forwarded, openForward } from './Forward';
import { AutoModAlert } from './AutoModAlert';
import {
  contentLength,
  decodeMentions,
  encodeMentions,
  mentionableRoles,
  useMaxContent,
} from './Composer';
// threads (2026-10-29): the bar under a starter message, "started a thread", Create thread
import { threadMessageItems, ThreadToolButton } from '../threads/entry';
import { ThreadBar, ThreadCreatedRow } from '../threads/ThreadBits';

export interface MsgPerms {
  manage: boolean;
  pin: boolean;
  /** Add a new reaction (Add Reactions). */
  react: boolean;
  /** Join a reaction someone already added (no Add Reactions needed, as on Discord and our server); `react` when absent. */
  joinReact?: boolean;
  send: boolean;
}

export interface MsgCtx {
  channel: Channel;
  resolver: MentionResolver;
  perms: MsgPerms;
  onReply(m: Message): void;
  onJump(id: Snowflake): void;
}

/** The one message being edited (↑ in an empty composer starts it; Discord edits one at a time). */
export const useEditing = create<{ id: Snowflake | null }>(() => ({ id: null }));
export const startEdit = (id: Snowflake) => useEditing.setState({ id });
const stopEdit = (id: Snowflake) => {
  if (useEditing.getState().id === id) useEditing.setState({ id: null });
};

export function useNameStyle(userId: Snowflake, guildId?: Snowflake): CSSProperties | undefined {
  const color = useData((d) => {
    if (!guildId) return 0;
    const r = colorRole(d.guilds[guildId], d.members[guildId]?.[userId]);
    return r?.color ?? 0;
  });
  if (!color) return undefined;
  const [l, dk] = roleColorPair(color);
  return { '--rc-l': l, '--rc-d': dk } as CSSProperties;
}

function isImage(a: Attachment): boolean {
  return /^image\//.test(a.content_type);
}

/* ------------------------------------------------------------ lightbox (I26) */

// the viewer's state lives in imageViewer.ts (the profile card opens avatars in it too)
const useLightbox = useImageViewer;

/** The channel's images, oldest first, for ←/→ in the lightbox. */
function channelImages(channelId: Snowflake): Attachment[] {
  const cm = useData.getState().messages[channelId];
  if (!cm) return [];
  return cm.ids.flatMap((id) => (cm.byId[id]?.attachments ?? []).filter(isImage));
}

export function openLightbox(channelId: Snowflake, a: Attachment, from: HTMLElement | null) {
  const all = channelImages(channelId);
  const list = all.some((x) => x.id === a.id) ? all : [a];
  useLightbox.setState({ open: { list, index: list.findIndex((x) => x.id === a.id), from } });
}

/**
 * The image viewer: --scrim-media, the image at most 90vw x 82vh, its file
 * name and "Open original" below, ←/→ through the channel's images. In
 * 180ms (scale .96 + fade), out 120ms. Not a dialog: its own layer.
 * Phones and tablets (§12.7 A6): the whole screen inside the safe area, on
 * the back stack (Back closes it); by touch a pinch zooms, a swipe sideways
 * goes through the pictures and a swipe down closes it (lightboxTouch.ts).
 */
export function Lightbox() {
  const { t, fmt } = useT();
  const open = useLightbox((s) => s.open);
  const [closing, setClosing] = useState(false);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const layer = useRef<HTMLDivElement>(null);
  const pic = useRef<HTMLImageElement>(null);
  const close = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => {
      const from = useLightbox.getState().open?.from;
      useLightbox.setState({ open: null });
      setClosing(false);
      from?.focus?.();
    }, 120);
  };
  const step = (d: number) =>
    useLightbox.setState((s) =>
      s.open && s.open.list.length > 1
        ? {
            open: {
              ...s.open,
              index: (s.open.index + d + s.open.list.length) % s.open.list.length,
            },
          }
        : s,
    );
  useEffect(() => {
    if (!open) return;
    closeBtn.current?.focus();
    const pop = pushEscape(close);
    // phones and tablets: Back closes it
    const offBack = readLayout().layout !== 'desktop' ? pushBack(close) : null;
    const key = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
      else return;
      e.preventDefault();
    };
    document.addEventListener('keydown', key);
    return () => {
      pop();
      offBack?.();
      document.removeEventListener('keydown', key);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per opening
  }, [!!open]);
  useLightboxTouch(layer, pic, { step, close }, open ? open.index : null);
  if (!open) return null;
  const a = open.list[open.index];
  if (!a) return null;
  const many = open.list.length > 1;
  const person = open.person;
  return createPortal(
    <div
      ref={layer}
      className={
        'lb' + (closing ? ' is-closing' : '') + (person ? ` lb-person lb-${person.kind}` : '')
      }
      role="dialog"
      aria-modal="true"
      aria-label={a.filename}
    >
      <div className="lb-scrim" onClick={close} />
      <figure className="lb-fig">
        <img
          ref={pic}
          key={a.id}
          src={assetUrl(a.url) ?? ''}
          alt={a.description ?? a.filename}
          style={a.width && a.height ? { aspectRatio: `${a.width} / ${a.height}` } : undefined}
        />
        <figcaption>
          {/* a file name reads left to right; a person's name keeps its own direction */}
          <span dir={person ? 'auto' : 'ltr'} className="lb-name">
            {a.filename}
          </span>
          {many ? (
            <span className="lb-pos nx-num">
              {fmt.num(open.index + 1)} / {fmt.num(open.list.length)}
            </span>
          ) : null}
          <a href={assetUrl(a.url) ?? '#'} target="_blank" rel="noopener noreferrer">
            {t('chat.lb.original')}
          </a>
        </figcaption>
      </figure>
      {many ? (
        <>
          <button
            type="button"
            className="lb-nav prev"
            aria-label={t('chat.lb.prev')}
            onClick={() => step(-1)}
          >
            <Icon name="chev-left" size="l" />
          </button>
          <button
            type="button"
            className="lb-nav next"
            aria-label={t('chat.lb.next')}
            onClick={() => step(1)}
          >
            <Icon name="chev-right" size="l" />
          </button>
        </>
      ) : null}
      <button
        ref={closeBtn}
        type="button"
        className="lb-x"
        aria-label={t('c.close')}
        onClick={close}
      >
        <Icon name="x" size="m" />
      </button>
    </div>,
    portalRoot(),
  );
}

/* ------------------------------------------------------------ attachments */

function ChatImage({ a, channelId }: { a: Attachment; channelId: Snowflake }) {
  const { t } = useT();
  const [loaded, setLoaded] = useState(false);
  const open = (e: MouseEvent<HTMLAnchorElement>) => {
    // Ctrl/Cmd/Shift-click and the middle button open the browser, as links do
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    openLightbox(channelId, a, e.currentTarget);
  };
  return (
    <a
      className={'msg-img ns-img' + (loaded ? ' is-loaded' : '')}
      href={assetUrl(a.url) ?? '#'}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('hub.img.open', { name: a.filename })}
      onClick={open}
      style={
        a.width && a.height
          ? // its own width at most: a small image is never blown up (QA 2026-10-28);
            // the box keeps the image's own shape inside 400 x 320
            ({
              aspectRatio: `${a.width} / ${a.height}`,
              '--img-w': `${a.width}px`,
              '--img-ar': `${a.width / a.height}`,
            } as CSSProperties)
          : undefined
      }
    >
      <img
        src={assetUrl(a.proxy_url || a.url) ?? ''}
        alt={a.description ?? a.filename}
        loading="lazy"
        onLoad={() => setLoaded(true)}
      />
      <span className="msg-img-name" dir="ltr">
        {a.filename}
      </span>
    </a>
  );
}

function Attachments({ list, channelId }: { list: Attachment[]; channelId: Snowflake }) {
  return (
    <>
      {list.map((a) =>
        isImage(a) ? (
          <ChatImage key={a.id} a={a} channelId={channelId} />
        ) : (
          <a
            key={a.id}
            className="ns-file"
            href={assetUrl(a.url) ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            dir="ltr"
          >
            <Icon name="paperclip" size="s" />
            {a.filename}
          </a>
        ),
      )}
    </>
  );
}

/* ------------------------------------------------------------ reactions */

function Reactions({ m, ctx }: { m: Message; ctx: MsgCtx }) {
  const { t } = useT();
  if (!m.reactions?.length) return null;
  return (
    <div className="rx" role="group">
      {/* each chip names who reacted on hover, and lists them on right-click (reactions.tsx) */}
      {m.reactions.map((r) => (
        <ReactionChip
          key={r.emoji.id ?? r.emoji.name ?? ''}
          m={m}
          r={r}
          guildId={ctx.channel.guild_id}
          canJoin={ctx.perms.joinReact ?? ctx.perms.react}
        />
      ))}
      {ctx.perms.react ? (
        <button
          type="button"
          className="rx-add"
          aria-label={t('hub.react.add')}
          data-tip={t('hub.react.add')}
          onClick={(ev) =>
            openEmoji(
              ev.currentTarget,
              (e) => void api.channels.react(m.channel_id, m.id, e).catch(reportError),
              'top-start',
            )
          }
        >
          <Icon name="smile-plus" size="s" />
        </button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------ edit */

/**
 * What the edit box may turn back into mentions: the people, channels and
 * roles of the message's place, plus whoever the message already mentions.
 */
function editNames(m: Message, guildId: Snowflake | undefined) {
  const d = useData.getState();
  const ch = d.channels[m.channel_id];
  const users = [
    ...(guildId
      ? Object.values(d.members[guildId] ?? {}).map((x) => x.user)
      : (ch?.recipients ?? [])),
    ...m.mentions,
  ];
  const channels = guildId ? Object.values(d.channels).filter((c) => c.guild_id === guildId) : [];
  const guild = guildId ? d.guilds[guildId] : undefined;
  const canEveryone = !!ch && can(channelPermissions(d, ch), Permissions.MENTION_EVERYONE);
  const allowed = mentionableRoles(guild, canEveryone);
  // a role the message already mentions stays a mention when it is saved again
  const kept = (guild?.roles ?? []).filter(
    (r) => m.content.includes(`<@&${r.id}>`) && !allowed.includes(r),
  );
  return { users, channels, roles: [...allowed, ...kept] };
}

/** The inline edit box (P5): the composer's skin, sized to its text, 44 up to half the window. */
function EditBox({ m, guildId, onDone }: { m: Message; guildId?: Snowflake; onDone(): void }) {
  const { t, fmt } = useT();
  // the console's limits.message_length, as in the composer
  const maxLength = useMaxContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the names when editing starts
  const names = useMemo(() => editNames(m, guildId), [m.id, guildId]);
  const [v, setV] = useState(() =>
    decodeCustomEmoji(decodeMentions(m.content, names.users, names.channels, names.roles)),
  );
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, Math.round(window.innerHeight / 2)) + 'px';
  }, [v]);
  const save = async () => {
    const text = v.trim();
    if (!text) {
      onDone();
      return;
    }
    // typed @mentions become real mentions, as in the composer
    const content = encodeCustomEmoji(
      encodeMentions(text, names.users, names.channels, names.roles),
      usableCustomEmoji(),
    );
    if (content === m.content.trim()) return onDone();
    if (contentLength(content) > maxLength) {
      toast(t('ns.cmp.tooLong', { n: fmt.num(maxLength) }), { icon: 'alert', tone: 'warn' });
      ref.current?.focus();
      return;
    }
    setBusy(true);
    try {
      await editMessage(m.channel_id, m.id, content);
      onDone();
    } catch (e) {
      reportError(e);
      setBusy(false);
      // still editing: the field keeps the focus, so Enter and Esc work at once
      ref.current?.focus();
    }
  };
  return (
    <div className="ns-edit">
      <div className="ns-edit-box">
        <textarea
          ref={ref}
          className="ns-edit-in"
          dir="auto"
          value={v}
          rows={1}
          aria-label={t('ns.msg.edit')}
          readOnly={busy}
          aria-busy={busy || undefined}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              if (!busy) void save();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              onDone();
            }
          }}
        />
      </div>
      {readLayout().pointer === 'coarse' ? (
        // a finger has no Esc key: Cancel · Save (§12.7 A4)
        <small className="ns-edit-hint">
          <button type="button" onClick={onDone}>
            {t('c.cancel')}
          </button>{' '}
          ·{' '}
          <button type="button" onClick={() => void save()}>
            {t('c.save')}
          </button>
        </small>
      ) : (
        // "Esc cancels · Enter saves", the key names as links (P5)
        <small className="ns-edit-hint">
          <button type="button" onClick={onDone}>
            {t('chat.edit.esc')}
          </button>{' '}
          {t('chat.edit.cancels')} ·{' '}
          <button type="button" onClick={() => void save()}>
            {t('chat.edit.enter')}
          </button>{' '}
          {t('chat.edit.saves')}
        </small>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ the row */

export const MessageRow = memo(function MessageRow({
  m,
  cont,
  ctx,
  isNew,
  replying,
}: {
  m: Message;
  cont: boolean;
  ctx: MsgCtx;
  isNew?: boolean;
  /** The composer is replying to this message (P23). */
  replying?: boolean;
}) {
  const { t, fmt } = useT();
  const me = useData((d) => d.me);
  const guildId = ctx.channel.guild_id;
  const author = useData((d) => d.users[m.author.id] ?? m.author);
  const name = useData((d) => userName(d, m.author.id, guildId));
  const nameStyle = useNameStyle(m.author.id, guildId);
  const refStyle = useNameStyle(m.referenced_message?.author.id ?? '', guildId);
  const isOwner = useData((d) => (guildId ? d.guilds[guildId]?.owner_id === m.author.id : false));
  const editing = useEditing((s) => s.id === m.id);
  const setEditing = (on: boolean) => (on ? startEdit(m.id) : stopEdit(m.id));
  const [toolsIn, setToolsIn] = useState(false);
  // the hover toolbar is built once the message is first hovered or focused (as Discord
  // does): built for every message it was half of the chat's DOM, and never shown on touch
  const [toolsOn, setToolsOn] = useState(false);
  const mine = m.author.id === me?.id;
  // the operator can turn editing off (admin-dashboard §4.9); the server refuses it too.
  // A poll and a forward are never edited (Discord).
  const canEdit = useFlag('message_edit') && mine && !m.poll && !m.message_snapshots?.length;
  const fwd = useFlag('message_forwarding') && canForward(m);
  const ignored = useUm((s) => !mine && !!s.ignored[m.author.id]);
  // §3.2: someone you blocked is collapsed to "Blocked message · Show"
  const blocked = useData(
    (d) => !mine && d.relationships[m.author.id]?.type === RelationshipType.BLOCKED,
  );
  // @everyone / @here is a mention only when it really pinged (the message says so)
  const resolver = useMemo(
    () => ({ ...ctx.resolver, mentionEveryone: m.mention_everyone }),
    [ctx.resolver, m.mention_everyone],
  );
  const revealed = useUm((s) => !!s.revealed[m.id]);
  // who reacted (the console's reaction_details): the menu's "View reactions"
  const reactionDetails = useFlag('reaction_details');
  // the text parsed and drawn once per content (and per names it shows), not on
  // every re-render of the row: a hover, the toolbar, a reaction
  const spoiler = t('hub.spoiler');
  const textNodes = useMemo(
    () => (m.content ? renderMarkdown(m.content, resolver, spoiler) : null),
    [m.content, resolver, spoiler],
  );
  const mentioned =
    !mine &&
    (m.mention_everyone ||
      m.mentions.some((u) => u.id === me?.id) ||
      (!!guildId &&
        m.mention_roles.length > 0 &&
        m.mention_roles.some((r) =>
          useData.getState().members[guildId]?.[me?.id ?? '']?.roles.includes(r),
        )));

  if (m.type === MessageType.THREAD_CREATED) return <ThreadCreatedRow m={m} />;
  // "{name}'s poll … has closed" (polls, 2026-10-29)
  if (m.type === MessageType.POLL_RESULT)
    return <PollResultNotice m={m} guildId={guildId} onJump={ctx.onJump} />;
  // an AutoMod alert (type 24, 2026-10-29): its own card with quick actions
  if (m.type === MessageType.AUTO_MODERATION_ACTION) return <AutoModAlert m={m} ctx={ctx} />;
  // a DM call's line (2026-11-01)
  if (m.type === MessageType.CALL) return <CallMessage m={m} />;

  if (
    m.type === MessageType.USER_JOIN ||
    m.type === MessageType.RECIPIENT_ADD ||
    m.type === MessageType.CHANNEL_PINNED_MESSAGE
  ) {
    const key =
      m.type === MessageType.USER_JOIN
        ? 'ns.sys.join'
        : m.type === MessageType.RECIPIENT_ADD
          ? 'ns.sys.added'
          : 'ns.sys.pinned';
    return (
      <article className="msg sys" data-mid={m.id} id={`msg-${m.id}`} tabIndex={-1}>
        <div className="msg-gut">
          <span className="sys-ic">
            <Icon
              name={m.type === MessageType.CHANNEL_PINNED_MESSAGE ? 'pin' : 'arrow-in'}
              size="s"
            />
          </span>
        </div>
        <div className="msg-body">
          <p className="sys-t">
            <span>
              <button
                type="button"
                className="sys-name"
                onClick={(e) => openProfile(m.author.id, e.currentTarget)}
              >
                <bdi>{name}</bdi>
              </button>{' '}
              {t(key)}
            </span>{' '}
            <time className="msg-time" dateTime={m.timestamp}>
              {fmt.stamp(m.timestamp)}
            </time>
          </p>
        </div>
      </article>
    );
  }

  if ((ignored || blocked) && !revealed) {
    return (
      <article
        className={'msg sys ns-ignored' + (blocked ? ' ns-blocked' : '')}
        data-mid={m.id}
        id={`msg-${m.id}`}
        tabIndex={-1}
        onKeyDown={(e) => {
          // the list's keys reach a collapsed message too: Enter shows it
          if (e.target === e.currentTarget && e.key === 'Enter') {
            e.preventDefault();
            revealMessage(m.id, true);
          }
        }}
      >
        <div className="msg-gut">
          <span className="sys-ic">
            <Icon name={blocked ? 'shield' : 'eye'} size="s" />
          </span>
        </div>
        <div className="msg-body">
          <button type="button" className="ns-link" onClick={() => revealMessage(m.id, true)}>
            {t(blocked ? 'block.message' : 'ignore.message')}
          </button>
        </div>
      </article>
    );
  }

  const ref = m.referenced_message;
  const refName = ref ? userName(useData.getState(), ref.author.id, guildId) : '';
  const react = (e: string) => {
    pushRecent(e);
    void api.channels.react(m.channel_id, m.id, e).catch(reportError);
  };
  const pinToggle = () =>
    void (m.pinned ? api.channels.unpin(m.channel_id, m.id) : api.channels.pin(m.channel_id, m.id))
      .then(() => toast(t(m.pinned ? 'ns.msg.unpinned' : 'ns.msg.pinned'), { icon: 'pin' }))
      .catch(reportError);
  const askDelete = async () => {
    const ok = await confirmDialog({
      title: t('ns.msg.deleteTitle'),
      body: t('ns.msg.deleteBody'),
      confirmLabel: t('ns.msg.delete'),
      danger: true,
      icon: 'trash',
    });
    if (ok) void deleteMessage(m.channel_id, m.id).catch(reportError);
  };
  const askEndPoll = async () => {
    const ok = await confirmDialog({
      title: t('pl.endTitle'),
      body: t('pl.endBody'),
      confirmLabel: t('pl.end'),
      icon: 'poll',
    });
    if (ok) void endPoll(m).catch(reportError);
  };
  const canDelete = mine || ctx.perms.manage;
  const more = (anchor: Element) => {
    openMenu(
      anchor,
      [
        // a finger has no hover toolbar: its menu (an action sheet on a phone or a
        // touch tablet) holds the toolbar's reaction too (§12.7 A4)
        ...(ctx.perms.react && (menusAsSheets() || readLayout().pointer === 'coarse')
          ? [
              {
                label: t('hub.msg.react'),
                icon: 'smile-plus' as const,
                run: () => openEmoji(anchor, react, 'bottom-end'),
              },
            ]
          : []),
        ...(canEdit
          ? [{ label: t('ns.msg.edit'), icon: 'edit' as const, run: () => setEditing(true) }]
          : []),
        ...(ctx.perms.send
          ? [{ label: t('hub.msg.reply'), icon: 'reply' as const, run: () => ctx.onReply(m) }]
          : []),
        ...threadMessageItems(t, m, ctx.channel),
        ...(fwd
          ? [{ label: t('fw.forward'), icon: 'forward' as const, run: () => openForward(m) }]
          : []),
        ...(m.poll && !pollOver(m.poll) && (mine || ctx.perms.manage)
          ? [{ label: t('pl.end'), icon: 'clock' as const, run: () => void askEndPoll() }]
          : []),
        ...(ctx.perms.pin
          ? [
              {
                label: t(m.pinned ? 'ns.msg.unpin' : 'ns.msg.pin'),
                icon: 'pin' as const,
                run: pinToggle,
              },
            ]
          : []),
        {
          label: t('ns.msg.copy'),
          icon: 'copy' as const,
          // the text as shown (names, not IDs); a blocked clipboard says so
          run: () => {
            const failed = () => toast(t('ns.msg.copyFailed'), { icon: 'alert', tone: 'warn' });
            if (!navigator.clipboard) return failed();
            void navigator.clipboard
              .writeText(
                plainText(
                  m.content || m.message_snapshots?.[0]?.message.content || '',
                  ctx.resolver,
                ),
              )
              .then(() => toast(t('c.copied'), { icon: 'check' }), failed);
          },
        },
        // everyone who reacted, per emoji (Discord's "View Reactions")
        ...(reactionDetails && m.reactions?.length
          ? [
              {
                label: t('rx.view'),
                icon: 'smile' as const,
                run: () => openReactionList(m.channel_id, m.id, m.reactions?.[0]?.emoji.name ?? ''),
              },
            ]
          : []),
        // Discord's "Report Message" (Console v2): never on your own
        ...(!mine
          ? [
              { sep: true },
              {
                label: t('rp.reportMessage'),
                icon: 'flag' as const,
                danger: true,
                run: () => openReport({ kind: 'message', message: m }),
              },
            ]
          : []),
        ...(canDelete
          ? [
              { sep: true },
              {
                label: t('ns.msg.delete'),
                icon: 'trash' as const,
                danger: true,
                run: () => void askDelete(),
              },
            ]
          : []),
      ],
      { placement: 'bottom-end' },
    );
  };

  // the focused message's own keys (I10); only when the message itself has the focus
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.target !== e.currentTarget || e.ctrlKey || e.metaKey || e.altKey || editing) return;
    const el = e.currentTarget;
    const k = e.key;
    const tools = () => el.querySelector<HTMLElement>('.msg-tools') ?? el;
    if (k === 'ContextMenu' || (k === 'F10' && e.shiftKey)) more(tools());
    else if ((k === 'r' || k === 'R') && ctx.perms.send) ctx.onReply(m);
    else if ((k === 'e' || k === 'E') && canEdit) setEditing(true);
    else if ((k === '+' || k === '=') && ctx.perms.react) openEmoji(tools(), react, 'bottom-end');
    else if ((k === 'p' || k === 'P') && ctx.perms.pin) pinToggle();
    else if ((k === 'Delete' || k === 'Backspace') && canDelete) void askDelete();
    else return;
    e.preventDefault();
  };
  // the toolbar moves inside the message when it would leave the scroller's top (I9)
  const placeTools = (el: HTMLElement) => {
    if (!toolsOn) setToolsOn(true);
    const sc = el.closest('.msgs');
    if (!sc) return;
    const inside = el.getBoundingClientRect().top - sc.getBoundingClientRect().top < 24;
    if (inside !== toolsIn) setToolsIn(inside);
  };
  const jumbo = !!m.content && isJumbo(m.content);
  const quick = ctx.perms.react ? quickReactions() : [];

  return (
    <article
      className={
        'msg' +
        (cont ? ' cont' : '') +
        (mentioned ? ' mentioned' : '') +
        (mine ? ' mine' : '') +
        (isNew ? ' nx-new' : '') +
        (replying ? ' is-reply-target' : '') +
        (editing ? ' is-editing' : '')
      }
      data-mid={m.id}
      id={`msg-${m.id}`}
      tabIndex={-1}
      data-tools={toolsIn ? 'in' : undefined}
      onMouseEnter={(e) => placeTools(e.currentTarget)}
      onFocus={(e) => placeTools(e.currentTarget)}
      onKeyDown={onKey}
      onContextMenu={(e) => {
        if ((e.target as HTMLElement).closest('a, textarea')) return;
        e.preventDefault();
        more({ x: e.clientX, y: e.clientY } as unknown as Element);
      }}
    >
      {ref !== undefined && m.message_reference ? (
        <div className="msg-refrow">
          <span className="msg-ref-line" aria-hidden="true" />
          {ref ? (
            <button
              type="button"
              className="msg-ref"
              aria-label={t('hub.msg.jump')}
              onClick={() => ctx.onJump(ref.id)}
            >
              <Avatar user={ref.author} size={16} />
              <b className={'msg-ref-n' + (refStyle ? ' nx-rc' : '')} style={refStyle} dir="auto">
                {refName}
              </b>
              <span className="msg-ref-t" dir="auto">
                {plainText(ref.content, ctx.resolver) ||
                  ref.poll?.question.text ||
                  plainText(ref.message_snapshots?.[0]?.message.content ?? '', ctx.resolver) ||
                  (ref.attachments.length ? '🖼️' : '')}
              </span>
            </button>
          ) : (
            <span className="msg-ref ns-ref-gone">{t('ns.msg.refGone')}</span>
          )}
        </div>
      ) : null}
      <div className="msg-gut">
        {cont ? (
          <time
            className="msg-htime nx-num"
            dateTime={m.timestamp}
            data-tip={fmt.stamp(m.timestamp)}
          >
            {fmt.time(m.timestamp)}
          </time>
        ) : (
          <button
            type="button"
            className="msg-av"
            tabIndex={-1}
            aria-hidden="true"
            onClick={(e) => openProfile(m.author.id, e.currentTarget)}
          >
            <Avatar user={author} name={name} size={40} />
          </button>
        )}
      </div>
      <div className="msg-body">
        {!cont ? (
          <header className="msg-h">
            <button
              type="button"
              className={'msg-name' + (nameStyle ? ' nx-rc' : '')}
              style={nameStyle}
              dir="auto"
              onClick={(e) => openProfile(m.author.id, e.currentTarget)}
            >
              {name}
            </button>
            {guildId ? <RoleIcon guildId={guildId} userId={m.author.id} cls="msg-ri" /> : null}
            {author.bot ? (
              <span className="msg-bot">
                <Icon name="check" />
                {t('hub.msg.bot')}
              </span>
            ) : null}
            {isOwner ? (
              <span className="msg-crown" data-tip={t('hub.mem.owner')}>
                <Icon name="crown" size="s" />
              </span>
            ) : null}
            <time
              className="msg-time"
              dateTime={m.timestamp}
              data-tip={`${fmt.date(m.timestamp, 'weekday')}, ${fmt.date(m.timestamp)} · ${fmt.time(m.timestamp)}`}
            >
              {fmt.stamp(m.timestamp)}
            </time>
            {m.pinned ? (
              <span className="msg-pin" data-tip={t('hub.msg.pinnedTag')}>
                <Icon name="pin" size="s" />
              </span>
            ) : null}
          </header>
        ) : null}
        {editing ? (
          <EditBox m={m} guildId={guildId} onDone={() => setEditing(false)} />
        ) : m.content ? (
          <div className={'msg-text' + (jumbo ? ' jumbo' : '')} dir="auto">
            {textNodes}
            {m.edited_timestamp ? (
              <>
                {' '}
                <span className="msg-edited" data-tip={fmt.stamp(m.edited_timestamp)}>
                  {t('hub.msg.edited')}
                </span>
              </>
            ) : null}
          </div>
        ) : null}
        {m.attachments.length ? (
          <Attachments list={m.attachments} channelId={m.channel_id} />
        ) : null}
        {m.message_snapshots?.length ? (
          <Forwarded
            m={m}
            resolver={resolver}
            onJump={ctx.onJump}
            media={
              m.message_snapshots[0]!.message.attachments.length ? (
                <Attachments
                  list={m.message_snapshots[0]!.message.attachments}
                  channelId={m.channel_id}
                />
              ) : null
            }
          />
        ) : null}
        {m.poll ? <PollView m={m} channel={ctx.channel} /> : null}
        <Reactions m={m} ctx={ctx} />
        <ThreadBar m={m} />
      </div>
      {!editing && toolsOn ? (
        <div className="msg-tools" role="toolbar" aria-label={t('hub.a11y.tools')}>
          {quick.map((e) => (
            <button
              key={e}
              type="button"
              className="mt-q"
              aria-label={e.startsWith('<') ? `:${emojiName(e)}:` : e}
              onClick={() => react(e)}
            >
              <Emoji e={e} />
            </button>
          ))}
          {quick.length ? <span className="mt-sep" aria-hidden="true" /> : null}
          {ctx.perms.react ? (
            <button
              type="button"
              aria-label={t('hub.msg.react')}
              data-tip={t('hub.msg.react')}
              onClick={(ev) => openEmoji(ev.currentTarget, react, 'bottom-end')}
            >
              <Icon name="smile-plus" size="m" />
            </button>
          ) : null}
          {ctx.perms.send ? (
            <button
              type="button"
              aria-label={t('hub.msg.reply')}
              data-tip={t('hub.msg.reply')}
              onClick={() => ctx.onReply(m)}
            >
              <Icon name="reply" size="m" />
            </button>
          ) : null}
          <ThreadToolButton m={m} channel={ctx.channel} />
          {fwd ? (
            <button
              type="button"
              aria-label={t('fw.forward')}
              data-tip={t('fw.forward')}
              onClick={() => openForward(m)}
            >
              <Icon name="forward" size="m" />
            </button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              aria-label={t('ns.msg.edit')}
              data-tip={t('ns.msg.edit')}
              onClick={() => setEditing(true)}
            >
              <Icon name="edit" size="m" />
            </button>
          ) : null}
          <button
            type="button"
            aria-label={t('hub.msg.more')}
            data-tip={t('hub.msg.more')}
            aria-haspopup="menu"
            onClick={(e) => more(e.currentTarget)}
          >
            <Icon name="more" size="m" />
          </button>
        </div>
      ) : null}
    </article>
  );
});
